import React, { useState, useEffect } from 'react';
import { ChevronLeft, X, Plus, Type, Square, Image as ImageIcon, Tag, Sparkles, Video as VideoIcon, Upload, Trash2 } from 'lucide-react';
import { overlayVideoAPI } from '../../services/api';

const OVERLAY_TYPES = {
  text: {
    id: 'text',
    name: 'Text Overlay',
    icon: <Type className="w-5 h-5" />,
    description: 'Add text overlays with custom styling',
    defaultConfig: {
      text: 'Sample Text',
      position: { x: 100, y: 100 },
      fontSize: 48,
      color: '#FFFFFF',
      backgroundColor: null,
      opacity: 1.0,
      start: 0,
      duration: 5
    }
  },
  shape: {
    id: 'shape',
    name: 'Shape Overlay',
    icon: <Square className="w-5 h-5" />,
    description: 'Add geometric shapes and arrows',
    defaultConfig: {
      shapeType: 'rectangle',
      position: { x: 100, y: 100 },
      size: { width: 200, height: 100 },
      color: '#FF0000',
      opacity: 0.5,
      borderWidth: 0,
      borderColor: '#000000',
      start: 0,
      duration: 5
    }
  },
  image: {
    id: 'image',
    name: 'Image Overlay',
    icon: <ImageIcon className="w-5 h-5" />,
    description: 'Add image overlays with transparency',
    defaultConfig: {
      imagePath: '',
      position: { x: 0, y: 0 },
      size: null,
      opacity: 1.0,
      start: 0,
      duration: 5
    }
  },
  video: {
    id: 'video',
    name: 'Video Overlay',
    icon: <VideoIcon className="w-5 h-5" />,
    description: 'Add video overlays with timeline control',
    defaultConfig: {
      videoPath: '',
      position: { x: 0, y: 0 },
      size: { width: 300, height: 200 },
      opacity: 1.0,
      start: 0,
      duration: 10,
      muted: true,
      loop: false,
      autoPlay: false
    }
  },
  lowerthird: {
    id: 'lowerthird',
    name: 'Lower Third',
    icon: <Tag className="w-5 h-5" />,
    description: 'Professional lower third graphics',
    defaultConfig: {
      title: 'Title',
      subtitle: 'Subtitle',
      position: 'bottom-left',
      backgroundColor: '#000000',
      textColor: '#FFFFFF',
      opacity: 0.8,
      start: 0,
      duration: 5
    }
  },
  particles: {
    id: 'particles',
    name: 'Particles',
    icon: <Sparkles className="w-5 h-5" />,
    description: 'Animated particle effects',
    defaultConfig: {
      particleCount: 50,
      particleColor: '#FFFFFF',
      particleSize: 3,
      animationSpeed: 1.0,
      start: 0,
      duration: 10
    }
  }
};

export default function VideoOverlaysPanel({ isOpen, onClose, template_content, settemplate_content }) {
  const [selectedOverlay, setSelectedOverlay] = useState(null);
  const [overlayVideos, setOverlayVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchOverlayVideos();
    }
  }, [isOpen]);

  const fetchOverlayVideos = async () => {
    try {
      const response = await overlayVideoAPI.getAll();
      if (response.data && response.data.success) {
        setOverlayVideos(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching overlay videos:', error);
    }
  };

  const handleUploadOverlayVideo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validExtensions = ['.mp4', '.mov', '.avi', '.mxf', '.m4v', '.3gp', '.flv', '.f4v', '.mkv', '.webm', '.m2v', '.mts', '.m2ts', '.vob', '.ts', '.mpeg', '.mpg'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(fileExt)) {
      alert(`Unsupported file format. Supported formats: ${validExtensions.join(', ')}`);
      return;
    }

    // Validate file size (200MB limit)
    const maxSize = 200 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`File too large. Maximum size is 200MB. Your file is ${(file.size / (1024 * 1024)).toFixed(1)}MB.`);
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('videoFile', file);
      if (file.name) {
        formData.append('name', file.name.replace(/\.[^/.]+$/, ''));
      }

      // Create XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(Math.round(percentComplete));
        }
      });

      const response = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({
              ok: true,
              status: xhr.status,
              json: () => Promise.resolve(JSON.parse(xhr.responseText))
            });
          } else {
            reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
          }
        };

        xhr.onerror = () => reject(new Error('Network error'));

        xhr.open('POST', '/api/v1/overlay-videos');
        xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);
        xhr.send(formData);
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success || (result.data && result.data.success)) {
          await fetchOverlayVideos();
          setShowUploadModal(false);
          alert('Overlay video uploaded successfully!');
        }
      }
    } catch (error) {
      console.error('Error uploading overlay video:', error);
      alert('Failed to upload overlay video');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (e && e.target) {
        e.target.value = ''; // Reset file input
      }
    }
  };

  const handleDeleteOverlayVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this overlay video?')) {
      return;
    }

    try {
      await overlayVideoAPI.delete(videoId);
      await fetchOverlayVideos();
      alert('Overlay video deleted successfully!');
    } catch (error) {
      console.error('Error deleting overlay video:', error);
      alert('Failed to delete overlay video');
    }
  };

  if (!isOpen) return null;

  const addOverlay = (overlayType) => {
    const overlayDef = OVERLAY_TYPES[overlayType];
    if (!overlayDef) return;

    // Create timeline item instead of global overlay
    const newItem = {
      id: `overlay_${Date.now()}`,
      type: overlayType,
      name: overlayDef.name,
      x: overlayDef.defaultConfig.position?.x || 100,
      y: overlayDef.defaultConfig.position?.y || 100,
      width: overlayDef.defaultConfig.size?.width || 200,
      height: overlayDef.defaultConfig.size?.height || 100,
      rotation: 0,
      opacity: overlayDef.defaultConfig.opacity || 1,
      layer: Math.max(...(template_content.pages[0]?.items?.map(item => item.layer || 0) || [0])) + 1,
      locked: false,
      time: {
        start: overlayDef.defaultConfig.start || 0,
        duration: overlayDef.defaultConfig.duration || 5
      },
      // Type-specific properties
      ...(overlayType === 'text' && {
        content: overlayDef.defaultConfig.text || 'Sample Text',
        fontSize: overlayDef.defaultConfig.fontSize || 48,
        color: overlayDef.defaultConfig.color || '#FFFFFF',
        backgroundColor: overlayDef.defaultConfig.backgroundColor || null
      }),
      ...(overlayType === 'shape' && {
        shapeType: overlayDef.defaultConfig.shapeType || 'rectangle',
        fill: overlayDef.defaultConfig.color || '#FF0000',
        borderWidth: overlayDef.defaultConfig.borderWidth || 0,
        borderColor: overlayDef.defaultConfig.borderColor || '#000000'
      }),
      ...(overlayType === 'image' && {
        src: overlayDef.defaultConfig.imagePath || '',
        alt: 'Overlay Image'
      }),
      ...(overlayType === 'video' && {
        videoPath: overlayDef.defaultConfig.videoPath || '',
        muted: overlayDef.defaultConfig.muted !== undefined ? overlayDef.defaultConfig.muted : true,
        loop: overlayDef.defaultConfig.loop !== undefined ? overlayDef.defaultConfig.loop : false,
        autoPlay: overlayDef.defaultConfig.autoPlay !== undefined ? overlayDef.defaultConfig.autoPlay : false
      })
    };

    const next = { ...template_content };

    // Add to the first page's items (assuming video templates have one page)
    if (!next.pages || next.pages.length === 0) {
      next.pages = [{
        id: 'main-page',
        name: 'Main Page',
        items: [],
        page_config: {
          name: 'Main Page',
          width: 1920,
          height: 1080,
          backgroundColor: '#000000'
        }
      }];
    }

    next.pages[0].items.push(newItem);

    settemplate_content(next);
    setSelectedOverlay(newItem.id);
  };

  const removeOverlay = (overlayId) => {
    const next = { ...template_content };

    // Remove from timeline items instead of global overlays
    if (next.pages && next.pages.length > 0) {
      next.pages[0].items = next.pages[0].items.filter(item => item.id !== overlayId);
    }

    settemplate_content(next);
    if (selectedOverlay === overlayId) {
      setSelectedOverlay(null);
    }
  };

  const updateOverlay = (overlayId, updates) => {
    const next = { ...template_content };

    // Update timeline item instead of global overlay
    if (next.pages && next.pages.length > 0) {
      const itemIndex = next.pages[0].items.findIndex(item => item.id === overlayId);
      if (itemIndex >= 0) {
        next.pages[0].items[itemIndex] = { ...next.pages[0].items[itemIndex], ...updates };
      }
    }

    settemplate_content(next);
  };

  // Get overlay items from timeline instead of global overlays
  const currentOverlays = template_content?.pages?.[0]?.items?.filter(item =>
    item.id?.startsWith('overlay_') || ['text', 'shape', 'image', 'video'].includes(item.type)
  ) || [];
  const selectedOverlayData = currentOverlays.find(overlay => overlay.id === selectedOverlay);

  return (
    <div className="fixed left-16 top-0 h-full w-80 bg-white border-r border-gray-200 shadow-lg z-50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold">Video Overlays</h2>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {!selectedOverlay ? (
          <>
            {/* Overlay Video Upload Section */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">Overlay Videos</h3>
                <button
                  onClick={() => setShowUploadModal(!showUploadModal)}
                  className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-1"
                >
                  <Upload className="w-3 h-3" />
                  <span>Upload</span>
                </button>
              </div>
              
              {showUploadModal && (
                <div className="mb-3 p-3 bg-gray-50 rounded border border-gray-200">
                  <label className="block text-xs text-gray-600 mb-2">Select Video File</label>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleUploadOverlayVideo}
                    disabled={uploading}
                    className="w-full text-xs"
                  />
                  {uploading && (
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Uploading... {uploadProgress}%</p>
                    </div>
                  )}
                </div>
              )}

              {/* Overlay Videos List */}
              {overlayVideos.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {overlayVideos.map((video) => (
                    <div key={video.id} className="bg-gray-50 rounded-lg p-2 flex items-center space-x-2">
                      {video.thumbnailUrl ? (
                        <img
                          src={video.thumbnailUrl}
                          alt={video.originalName}
                          className="w-12 h-12 object-cover rounded"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                          <VideoIcon className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-700 truncate">
                          {video.originalName || video.filename}
                        </div>
                        <div className="text-xs text-gray-500">
                          {video.width}x{video.height} • {video.duration ? `${video.duration}s` : 'N/A'}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteOverlayVideo(video.id)}
                        className="p-1 text-red-600 hover:bg-red-100 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500">No overlay videos uploaded yet</p>
              )}
            </div>

            {/* Overlay Types */}
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Add Overlay</h3>
              <div className="space-y-2">
                {Object.values(OVERLAY_TYPES).map((overlayType) => (
                  <button
                    key={overlayType.id}
                    onClick={() => addOverlay(overlayType.id)}
                    className="w-full p-3 rounded-lg border-2 border-gray-200 bg-white hover:border-blue-300 hover:shadow-md transition-all text-left"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="text-blue-600">
                        {overlayType.icon}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{overlayType.name}</div>
                        <div className="text-xs text-gray-500">{overlayType.description}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Current Overlays */}
            {currentOverlays.length > 0 && (
              <div className="border-t border-gray-200 p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Current Overlays</h3>
                <div className="space-y-2">
                  {currentOverlays.map((overlay) => {
                    const overlayDef = OVERLAY_TYPES[overlay.type];
                    return (
                      <div key={overlay.id} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="text-blue-600">
                              {overlayDef?.icon}
                            </div>
                            <div>
                              <div className="font-medium text-sm">{overlay.name || overlayDef?.name}</div>
                              <div className="text-xs text-gray-500">
                                {overlay.time?.start || 0}s - {(overlay.time?.start || 0) + (overlay.time?.duration || 5)}s
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => setSelectedOverlay(overlay.id)}
                              className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => removeOverlay(overlay.id)}
                              className="p-1 text-red-600 hover:bg-red-100 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        ) : (
          /* Overlay Editor */
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-700">
                Edit {OVERLAY_TYPES[selectedOverlayData?.type]?.name}
              </h3>
              <button
                onClick={() => setSelectedOverlay(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {selectedOverlayData && (
              <div className="space-y-4">
                {/* Timing Controls */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Start Time (s)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={selectedOverlayData.time?.start || 0}
                      onChange={(e) => updateOverlay(selectedOverlay, {
                        time: {
                          ...selectedOverlayData.time,
                          start: parseFloat(e.target.value) || 0
                        }
                      })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Duration (s)</label>
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={selectedOverlayData.time?.duration || 1}
                      onChange={(e) => updateOverlay(selectedOverlay, {
                        time: {
                          ...selectedOverlayData.time,
                          duration: parseFloat(e.target.value) || 1
                        }
                      })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                </div>

                {/* Position Controls */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">X Position</label>
                    <input
                      type="number"
                      value={selectedOverlayData.x || 0}
                      onChange={(e) => updateOverlay(selectedOverlay, { x: parseInt(e.target.value) || 0 })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Y Position</label>
                    <input
                      type="number"
                      value={selectedOverlayData.y || 0}
                      onChange={(e) => updateOverlay(selectedOverlay, { y: parseInt(e.target.value) || 0 })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                </div>

                {/* Type-specific controls */}
                {selectedOverlayData.type === 'text' && (
                  <>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Text</label>
                      <textarea
                        value={selectedOverlayData.content || ''}
                        onChange={(e) => updateOverlay(selectedOverlay, { content: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        rows="2"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Font Size</label>
                        <input
                          type="number"
                          min="12"
                          max="200"
                          value={selectedOverlayData.fontSize || 48}
                          onChange={(e) => updateOverlay(selectedOverlay, { fontSize: parseInt(e.target.value) || 48 })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Text Color</label>
                        <input
                          type="color"
                          value={selectedOverlayData.color || '#FFFFFF'}
                          onChange={(e) => updateOverlay(selectedOverlay, { color: e.target.value })}
                          className="w-full h-8 border border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  </>
                )}

                {selectedOverlayData.type === 'shape' && (
                  <>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Shape Type</label>
                      <select
                        value={selectedOverlayData.shapeType || 'rectangle'}
                        onChange={(e) => updateOverlay(selectedOverlay, { shapeType: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      >
                        <option value="rectangle">Rectangle</option>
                        <option value="circle">Circle</option>
                        <option value="arrow">Arrow</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Width</label>
                        <input
                          type="number"
                          min="10"
                          value={selectedOverlayData.width || 200}
                          onChange={(e) => updateOverlay(selectedOverlay, { width: parseInt(e.target.value) || 200 })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Height</label>
                        <input
                          type="number"
                          min="10"
                          value={selectedOverlayData.height || 100}
                          onChange={(e) => updateOverlay(selectedOverlay, { height: parseInt(e.target.value) || 100 })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Fill Color</label>
                      <input
                        type="color"
                        value={selectedOverlayData.fill || '#FF0000'}
                        onChange={(e) => updateOverlay(selectedOverlay, { fill: e.target.value })}
                        className="w-full h-8 border border-gray-300 rounded"
                      />
                    </div>
                  </>
                )}

                {selectedOverlayData.type === 'video' && (
                  <>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Video Source</label>
                      <select
                        value={selectedOverlayData.videoPath || ''}
                        onChange={(e) => updateOverlay(selectedOverlay, { videoPath: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      >
                        <option value="">Select overlay video...</option>
                        {overlayVideos.map((video) => (
                          <option key={video.id} value={video.storageUrl || video.id}>
                            {video.originalName || video.filename}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Width</label>
                        <input
                          type="number"
                          min="10"
                          value={selectedOverlayData.width || 300}
                          onChange={(e) => updateOverlay(selectedOverlay, { width: parseInt(e.target.value) || 300 })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Height</label>
                        <input
                          type="number"
                          min="10"
                          value={selectedOverlayData.height || 200}
                          onChange={(e) => updateOverlay(selectedOverlay, { height: parseInt(e.target.value) || 200 })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedOverlayData.muted !== undefined ? selectedOverlayData.muted : true}
                          onChange={(e) => updateOverlay(selectedOverlay, { muted: e.target.checked })}
                          className="rounded"
                        />
                        <span className="text-xs text-gray-600">Muted</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedOverlayData.loop !== undefined ? selectedOverlayData.loop : false}
                          onChange={(e) => updateOverlay(selectedOverlay, { loop: e.target.checked })}
                          className="rounded"
                        />
                        <span className="text-xs text-gray-600">Loop</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedOverlayData.autoPlay !== undefined ? selectedOverlayData.autoPlay : false}
                          onChange={(e) => updateOverlay(selectedOverlay, { autoPlay: e.target.checked })}
                          className="rounded"
                        />
                        <span className="text-xs text-gray-600">Auto Play</span>
                      </label>
                    </div>
                  </>
                )}

                {selectedOverlayData.type === 'lowerthird' && (
                  <>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Title</label>
                      <input
                        type="text"
                        value={selectedOverlayData.title || ''}
                        onChange={(e) => updateOverlay(selectedOverlay, { title: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Subtitle</label>
                      <input
                        type="text"
                        value={selectedOverlayData.subtitle || ''}
                        onChange={(e) => updateOverlay(selectedOverlay, { subtitle: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Position</label>
                      <select
                        value={selectedOverlayData.position || 'bottom-left'}
                        onChange={(e) => updateOverlay(selectedOverlay, { position: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      >
                        <option value="bottom-left">Bottom Left</option>
                        <option value="bottom-right">Bottom Right</option>
                        <option value="bottom-center">Bottom Center</option>
                      </select>
                    </div>
                  </>
                )}

                {/* Opacity Control */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Opacity: {Math.round((selectedOverlayData.opacity || 1) * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={selectedOverlayData.opacity || 1}
                    onChange={(e) => updateOverlay(selectedOverlay, { opacity: parseFloat(e.target.value) })}
                    className="w-full"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
